import LegalPageShell from "@/components/site/LegalPageShell";
import { useLanguage, pick } from "@/lib/i18n";
import { SUPPORT_EMAIL, OWNER_NAME, OWNER_ADDRESS } from "@/lib/translations";

const COPY = {
    en: {
        title: "Legal Notice (Impressum)",
        subtitle: "Information in accordance with § 5 TMG and § 18 MStV (Germany).",
        sections: [
            { id: "operator", label: "Operator" },
            { id: "responsible", label: "Responsible for content" },
            { id: "disputes", label: "EU dispute resolution" },
            { id: "liability-content", label: "Liability for content" },
            { id: "liability-links", label: "Liability for links" },
            { id: "copyright", label: "Copyright" },
        ],
    },
    de: {
        title: "Impressum",
        subtitle: "Angaben gemäß § 5 TMG und § 18 MStV.",
        sections: [
            { id: "operator", label: "Anbieter" },
            { id: "responsible", label: "Inhaltlich Verantwortlicher" },
            { id: "disputes", label: "EU-Streitbeilegung" },
            { id: "liability-content", label: "Haftung für Inhalte" },
            { id: "liability-links", label: "Haftung für Links" },
            { id: "copyright", label: "Urheberrecht" },
        ],
    },
};

export default function LegalNotice() {
    const { lang } = useLanguage();
    const t = pick(COPY, lang);
    return (
        <LegalPageShell title={t.title} subtitle={t.subtitle} sections={t.sections}>
            {lang === "en" ? <EN /> : <DE />}
        </LegalPageShell>
    );
}

function EN() {
    return (
        <>
            <h2 id="operator">Operator (Anbieter)</h2>
            <p>
                <strong>{OWNER_NAME}</strong><br />
                {OWNER_ADDRESS}<br />
                Email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </p>

            <h2 id="responsible">Responsible for editorial content according to § 18 (2) MStV</h2>
            <p>
                {OWNER_NAME}<br />
                {OWNER_ADDRESS}
            </p>

            <h2 id="disputes">EU Online Dispute Resolution</h2>
            <p>
                The European Commission provides a platform for online dispute resolution (ODR):{" "}
                <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
                    https://ec.europa.eu/consumers/odr
                </a>
                . The operator is not obliged and not willing to participate in dispute resolution proceedings before a consumer arbitration board.
            </p>

            <h2 id="liability-content">Liability for content</h2>
            <p>
                As a service provider, we are responsible for our own content on this website in accordance with general laws (§ 7 (1) TMG). However,
                under §§ 8 to 10 TMG, we are not obliged to monitor transmitted or stored third-party information or to investigate circumstances
                that indicate illegal activity. Obligations to remove or block the use of information under general law remain unaffected.
            </p>

            <h2 id="liability-links">Liability for links</h2>
            <p>
                Our website may contain links to external websites operated by third parties, over whose content we have no influence. We cannot
                therefore accept any liability for these external contents. The respective provider or operator of the linked pages is always
                responsible for their content. If we become aware of any legal violations, we will remove such links immediately.
            </p>

            <h2 id="copyright">Copyright</h2>
            <p>
                The content and works on this website created by the operator are subject to German copyright law. Duplication, processing,
                distribution and any kind of exploitation outside the limits of copyright require the written consent of the respective author
                or creator. Downloads and copies of this site are only permitted for private, non-commercial use.
            </p>
        </>
    );
}

function DE() {
    return (
        <>
            <h2 id="operator">Anbieter</h2>
            <p>
                <strong>{OWNER_NAME}</strong><br />
                {OWNER_ADDRESS}<br />
                E-Mail: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </p>

            <h2 id="responsible">Inhaltlich verantwortlich gemäß § 18 Abs. 2 MStV</h2>
            <p>
                {OWNER_NAME}<br />
                {OWNER_ADDRESS}
            </p>

            <h2 id="disputes">EU-Streitbeilegung</h2>
            <p>
                Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{" "}
                <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
                    https://ec.europa.eu/consumers/odr
                </a>
                . Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
            </p>

            <h2 id="liability-content">Haftung für Inhalte</h2>
            <p>
                Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich.
                Nach §§ 8 bis 10 TMG sind wir jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder
                nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung
                von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt.
            </p>

            <h2 id="liability-links">Haftung für Links</h2>
            <p>
                Unser Angebot enthält ggf. Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für
                diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder
                Betreiber der Seiten verantwortlich. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen.
            </p>

            <h2 id="copyright">Urheberrecht</h2>
            <p>
                Die durch den Anbieter erstellten Inhalte und Werke auf dieser Website unterliegen dem deutschen Urheberrecht. Die Vervielfältigung,
                Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechts bedürfen der schriftlichen Zustimmung
                des jeweiligen Autors bzw. Erstellers. Downloads und Kopien dieser Seite sind nur für den privaten, nicht-kommerziellen Gebrauch
                gestattet.
            </p>
        </>
    );
}
